import { components } from '@luxuryescapes/contract-trip/dist/server'

import { UpdateTripItemRequest } from 'tripPlanner/api/tripItem/types'
import {
  extractEditableEndDateTimeStrings,
  extractEditableStartDateTimeStrings,
} from 'tripPlanner/components/TripModal/TripItems/Edit/utils'
import { BasicTrip, FullTrip, Place } from 'tripPlanner/types/common'
import {
  AttractionItem,
  CustomItem,
  EventItem,
  NoteItem,
  RestaurantBarItem,
  ThingToDoItem,
  TripItem,
} from 'tripPlanner/types/tripItem'

// We were part-way through removing the `_MULTI_DATES` types from the service
// and never got around to finishing it, so we have to do crap like this.
function toMultiDatePlace(
  place: Place,
): components['schemas']['placeMultiDates'] {
  return place.type === 'GEO'
    ? {
        ...place,
        type: 'GEO_MULTI_DATES' as const,
      }
    : {
        ...place,
        type: 'CUSTOM_MULTI_DATES' as const,
      }
}

export function customItemToUpdatePayload(
  item: CustomItem,
): UpdateTripItemRequest {
  const {
    id,
    tripId,
    createdAt,
    updatedAt,
    userUpdatedAt,
    createdBy,
    updatedBy,
    startUtcDateTime,
    // @ts-ignore - Doesn't exist on all of these types, but we want to filter it out if it's there
    endUtcDateTime,
    sortableName,
    // @ts-ignore - Doesn't exist on all of these types, but we want to filter it out if it's there
    status,
    ...editableFields
  } = item

  if (
    editableFields.type === 'EVENT' ||
    editableFields.type === 'AIRBNB' ||
    editableFields.type === 'HOTEL'
  ) {
    return {
      ...editableFields,
      ...extractEditableStartDateTimeStrings(editableFields),
      ...extractEditableEndDateTimeStrings(editableFields),
      place: toMultiDatePlace(editableFields.place),
    }
  } else if (
    editableFields.type === 'ATTRACTION' ||
    editableFields.type === 'RESTAURANT_BAR'
  ) {
    return {
      ...editableFields,
      ...extractEditableStartDateTimeStrings(editableFields),
    }
  } else {
    return {
      ...editableFields,
      ...extractEditableStartDateTimeStrings(editableFields),
      ...extractEditableEndDateTimeStrings(editableFields),
    }
  }
}

export const itemTypesWithNoEndDate = [
  'ATTRACTION',
  'RESTAURANT_BAR',
] satisfies Array<
  Exclude<TripItem, { endDate: moment.Moment | undefined }>['type']
>

export function itemIsInItinerary(
  item: TripItem,
  trip: FullTrip | BasicTrip | undefined,
): boolean {
  return (
    !!item.isInItinerary &&
    !!item.startDate &&
    !!trip?.startDate &&
    !!trip.endDate
  )
}

export function itemIsVisibleInItinerary(
  item: TripItem,
  trip: FullTrip | BasicTrip | undefined,
): boolean {
  return (
    !!item.isInItinerary &&
    !!item.startDate &&
    !!trip?.startDate &&
    !!trip.endDate &&
    item.startDate.isBetween(trip.startDate, trip.endDate, 'day', '[]')
  )
}

export function canAddItemToItineraryWithExistingDate(
  item: TripItem,
  trip: FullTrip | BasicTrip | undefined,
): boolean {
  return (
    !item.isInItinerary &&
    !!item.startDate &&
    !!trip?.startDate &&
    !!trip.endDate &&
    item.startDate.isBetween(trip.startDate, trip.endDate, 'day', '[]')
  )
}

export function isCustomSingleDateItem(
  item: TripItem,
): item is
  | AttractionItem
  | EventItem
  | RestaurantBarItem
  | ThingToDoItem
  | NoteItem {
  return (
    item.type === 'ATTRACTION' ||
    item.type === 'EVENT' ||
    item.type === 'RESTAURANT_BAR' ||
    item.type === 'THING_TO_DO' ||
    item.type === 'NOTE'
  )
}
